import { apiGet, apiPost, apiPatch } from "./client";
import type { Pipeline, PipelineStage, PipelineAction, Job, Company, PipelineStatus } from "@career-ops-amplify/shared";

/** Simple hash function matching the backend's simpleHash for URL dedup. */
export function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
}

// ---- Pipeline-specific endpoints (stages, actions, workflow) ----

export interface PipelineDetail {
  pipeline: Pipeline;
  stages: PipelineStage[];
  actions: PipelineAction[];
  job?: Job;
  company?: Company;
}

export interface ListPipelinesResponse {
  pipelines: Pipeline[];
  total?: number;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
  nextCursor?: string;
}

/** Create a pipeline for a job. */
export async function createPipeline(jobId: string): Promise<{ pipeline: Pipeline }> {
  return apiPost(`/api/pipeline/for-job/${jobId}`);
}

/** List pipelines (supports DynamoDB cursor pagination). */
export async function listPipelines(params?: {
  limit?: number;
  cursor?: string;
  offset?: number;
}): Promise<ListPipelinesResponse> {
  return apiGet<ListPipelinesResponse>("/api/pipeline/list", params);
}

/** Get full pipeline detail (stages + actions + linked job + company). */
export async function getPipelineDetail(pipelineId: string): Promise<PipelineDetail> {
  return apiGet<PipelineDetail>(`/api/pipeline/detail/${pipelineId}`);
}

/** Update pipeline status. */
export async function updatePipelineStatus(
  pipelineId: string,
  status: PipelineStatus,
): Promise<{ pipeline: Pipeline }> {
  return apiPatch(`/api/pipeline/detail/${pipelineId}`, { status });
}

export async function archivePipeline(pipelineId: string): Promise<{ pipeline: Pipeline }> {
  return updatePipelineStatus(pipelineId, "archived");
}

export async function restorePipeline(
  pipelineId: string,
  status: PipelineStatus,
): Promise<{ pipeline: Pipeline }> {
  return updatePipelineStatus(pipelineId, status);
}

/** Advance pipeline to a new stage. */
export async function advanceStage(pipelineId: string, stageName: string): Promise<void> {
  await apiPatch(`/api/pipeline/detail/${pipelineId}/stage`, { stageName });
}

export interface PipelineRunResult {
  success: boolean;
  output?: string;
  score?: number;
  error?: string;
  action?: PipelineAction;
}

/** Run a prompt template for a pipeline stage. */
export async function runPipelinePrompt(
  pipelineId: string,
  input: { promptKind: string; stageName?: string },
): Promise<PipelineRunResult> {
  return apiPost<PipelineRunResult>(`/api/pipeline/detail/${pipelineId}/run`, input);
}

/** Record an action on a pipeline. */
export async function recordAction(
  pipelineId: string,
  action: { type: string; status?: string; result?: string; metadata?: Record<string, unknown>; stageId?: string },
): Promise<{ action: PipelineAction }> {
  return apiPost(`/api/pipeline/detail/${pipelineId}/actions`, action);
}

/** List actions for a pipeline. */
export async function listActions(pipelineId: string): Promise<{ actions: PipelineAction[]; total: number }> {
  return apiGet(`/api/pipeline/detail/${pipelineId}/actions`);
}